import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faClock,
  faComment,
  faFire,
  faDiagramProject,
  faThumbTack,
} from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import ProjectConfig from "@serie3/freelancesplaceapi/Config/Projects/ProjectConfig";

const sectionLabel = {
  fontSize: "0.62rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#b0b0b0",
  marginBottom: "6px",
};

const innerCard = {
  background: "#f8f9fb",
  borderRadius: "10px",
  padding: "14px 16px",
  flex: 1,
};

const SkeletonCard = () => (
  <div style={{
    background: "#f0f0f0", borderRadius: "10px", padding: "14px 16px", flex: 1,
    display: "flex", flexDirection: "column", gap: "8px",
  }}>
    {["60%", "80%", "40%"].map((w, i) => (
      <div key={i} style={{ height: "10px", width: w, background: "#e0e0e0", borderRadius: "4px" }} />
    ))}
  </div>
);

const ProjectDashboard = ({ stub, setDoneTickets, onUnpin }) => {
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!stub?.id) return;
    const load = async () => {
      const res = await crudApi.justGet(ProjectConfig.endpoints.show + stub.id);
      if (res.status === 200) {
        setProject(res.data);
        const actions = res.data.actions || [];
        if (setDoneTickets && actions.length > 0) {
          const done = actions.filter((a) => a.action.status === "Done").length;
          setDoneTickets(Math.round((done / actions.length) * 100));
        }
      }
    };
    load();
  }, [stub?.id]);

  const title = project?.title ?? stub?.title ?? "";
  const id    = project?.id    ?? stub?.id;

  const actions    = (project?.actions) || [];
  const total      = actions.length;
  const done       = actions.filter((a) => a.action.status === "Done").length;
  const inProgress = actions.filter((a) => a.action.status === "In_Progress").length;
  const todo       = actions.filter((a) => a.action.status === "Created").length;
  const onHold     = actions.filter((a) => a.action.status === "On_Hold").length;
  const canceled   = actions.filter((a) => a.action.status === "Canceled").length;
  const open       = todo + inProgress + onHold;
  const donePct    = total ? Math.round((done / total) * 100) : 0;

  const urgentOpen = actions.filter(
    (a) => ["Highest", "High"].includes(a.action.priority) &&
           !["Done", "Canceled"].includes(a.action.status)
  ).length;

  const totalMins  = (project?.tasks || []).reduce((s, t) => s + (t.task?.total_time || 0), 0);
  const totalHours = (totalMins / 60).toFixed(1);

  const unpin = async () => {
    if (!project) return;
    const payload = JSON.stringify({
      id: project.id,
      title: project.title,
      description: project.description,
      start: project.start,
      end: project.end,
      max_hours: project.max_hours,
      daily_hours: project.daily_hours,
      done: project.done,
      original_id: project.original_id,
      original_link: project.original_link,
      last_update: project.last_update,
      quicklinkList: project.quicklinkList,
      color: project.color || "",
      file_name: project.file_name || "",
      file_string: project.file_string || "",
      tags: project.tags || [],
      project_addresses: project.project_addresses || [],
      finance: (project.finance || []).map((f) => ({ id: f.id })),
      documents: (project.documents || []).map((d) => ({ id: d.id })),
      show_in_dashboard: false,
    });
    if (onUnpin) onUnpin();
    await crudApi.edit(ProjectConfig.endpoints.edit, payload, true);
  };

  const lastComment = project?.comments?.length
    ? [...project.comments].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      )[0]
    : null;

  const customer =
    project?.kunde?.firmenname ||
    (project?.person
      ? `${project.person.vorname || ""} ${project.person.name || ""}`.trim() || null
      : null);

  const chartData = total > 0 ? {
    labels: ["Todo", "In Progress", "On Hold", "Done", "Canceled"],
    datasets: [{
      data: [todo, inProgress, onHold, done, canceled],
      backgroundColor: ["#1a73e8", "#00acc1", "#9e9e9e", "#2e7d32", "#e53935"],
      borderColor: "#fff",
      borderWidth: 2,
      hoverOffset: 4,
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
  };

  const healthColor = donePct >= 75 ? "#2e7d32" : donePct >= 40 ? "#e65100" : "#1a73e8";

  return (
    <div style={{
      background: "#fff",
      borderRadius: "14px",
      boxShadow: "0 2px 14px rgba(0,0,0,0.07)",
      marginBottom: "16px",
      overflow: "hidden",
    }}>
      {/* Header bar */}
      <div style={{
        background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "34px", height: "34px", borderRadius: "9px",
            background: "rgba(255,255,255,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.9rem", color: "#fff", flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faDiagramProject} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff", lineHeight: 1.2 }}>
              {title}
            </div>
            {customer && (
              <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.65)", marginTop: "2px" }}>
                {customer}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            onClick={unpin}
            title="Remove from dashboard"
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "6px 13px",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.75)", borderRadius: "8px",
              fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,80,80,0.35)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "rgba(255,255,255,0.75)"; }}
          >
            <FontAwesomeIcon icon={faThumbTack} style={{ fontSize: "0.6rem" }} />
            Unpin
          </button>
          <Link
            to={`/project/edit/${id}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "6px 13px",
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff", borderRadius: "8px",
              fontSize: "0.78rem", fontWeight: 600, textDecoration: "none",
              transition: "background 0.15s",
            }}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: "0.6rem" }} />
            Open
          </Link>
        </div>
      </div>

      {/* 4-card content area */}
      <div style={{ padding: "14px 16px", display: "flex", gap: "12px" }}>

        {!project && <><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></>}

        {/* Card 1 — Health */}
        {project && <>
        <div style={innerCard}>
          <div style={sectionLabel}>Health</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", marginBottom: "10px" }}>
            <span style={{ fontSize: "2.2rem", fontWeight: 900, color: healthColor, lineHeight: 1 }}>
              {donePct}%
            </span>
            <span style={{ fontSize: "0.75rem", color: "#9e9e9e", paddingBottom: "4px" }}>done</span>
          </div>
          <div style={{ height: "5px", background: "#ebebeb", borderRadius: "3px", overflow: "hidden", marginBottom: "12px" }}>
            <div style={{
              height: "100%", width: `${donePct}%`,
              background: `linear-gradient(90deg, #1a73e8, ${healthColor})`,
              borderRadius: "3px", transition: "width 0.5s",
            }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem" }}>
              <span style={{ color: "#9e9e9e" }}>Open</span>
              <span style={{ fontWeight: 700, color: "#424242" }}>{open}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem" }}>
              <span style={{ color: "#9e9e9e" }}>Done</span>
              <span style={{ fontWeight: 700, color: "#2e7d32" }}>{done}</span>
            </div>
            {urgentOpen > 0 && (
              <div style={{
                marginTop: "6px", display: "flex", alignItems: "center", gap: "5px",
                padding: "5px 8px", background: "#fff3e0", borderRadius: "6px",
                fontSize: "0.75rem", color: "#e65100", fontWeight: 600,
              }}>
                <FontAwesomeIcon icon={faFire} style={{ fontSize: "0.7rem" }} />
                {urgentOpen} high priority open
              </div>
            )}
            {totalMins > 0 && (
              <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", color: "#9e9e9e" }}>
                <FontAwesomeIcon icon={faClock} style={{ fontSize: "0.65rem" }} />
                {totalHours} h tracked
              </div>
            )}
          </div>
        </div>

        {/* Card 2 — Last comment */}
        <div style={innerCard}>
          <div style={sectionLabel}>
            <FontAwesomeIcon icon={faComment} style={{ marginRight: "4px" }} />Last Comment
          </div>
          {lastComment ? (
            <>
              <p style={{
                fontSize: "0.82rem", color: "#424242", margin: 0, lineHeight: 1.55,
                overflow: "hidden", display: "-webkit-box",
                WebkitLineClamp: 6, WebkitBoxOrient: "vertical",
              }}>
                {lastComment.value || lastComment.comment || lastComment.beschreibung || "—"}
              </p>
              {lastComment.created_at && (
                <div style={{ marginTop: "8px", fontSize: "0.68rem", color: "#bdbdbd" }}>
                  {new Date(lastComment.created_at).toLocaleDateString()}
                </div>
              )}
            </>
          ) : (
            <p style={{ fontSize: "0.82rem", color: "#bdbdbd", margin: 0 }}>No comments yet</p>
          )}
        </div>

        {/* Card 3 — Ticket chart */}
        <div style={innerCard}>
          <div style={sectionLabel}>Tickets</div>
          {chartData ? (
            <>
              <div style={{ position: "relative", height: "120px" }}>
                <Doughnut data={chartData} options={chartOptions} />
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginTop: "10px" }}>
                {[
                  { name: "Todo",     color: "#1a73e8", count: todo },
                  { name: "Progress", color: "#00acc1", count: inProgress },
                  { name: "Done",     color: "#2e7d32", count: done },
                  { name: "Hold",     color: "#9e9e9e", count: onHold },
                ].filter((i) => i.count > 0).map(({ name, color, count }) => (
                  <div key={name} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.7rem", color: "#616161" }}>
                    <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
                    {name} {count}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ fontSize: "0.82rem", color: "#bdbdbd", margin: 0 }}>No tickets yet</p>
          )}
        </div>

        {/* Card 4 — Customer + link */}
        <div style={innerCard}>
          <div style={sectionLabel}>Project Info</div>
          {customer && (
            <div style={{ marginBottom: "12px" }}>
              <div style={{ fontSize: "0.68rem", color: "#bdbdbd", marginBottom: "2px" }}>Customer</div>
              <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#212121" }}>{customer}</div>
            </div>
          )}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "0.68rem", color: "#bdbdbd", marginBottom: "4px" }}>Total tickets</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#212121", lineHeight: 1 }}>{total}</div>
          </div>
          <Link
            to={`/project/edit/${id}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "7px 14px", background: "#e8f0fe", color: "#1a73e8",
              borderRadius: "8px", fontSize: "0.8rem", fontWeight: 600,
              textDecoration: "none",
            }}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: "0.62rem" }} />
            Open Project
          </Link>
        </div>
        </>}

      </div>
    </div>
  );
};

export default ProjectDashboard;
